import React, { useState, useEffect } from 'react';
import { Bell, BellOff, CheckCircle, XCircle, AlertCircle, Send } from 'lucide-react';
import {
  isPushNotificationSupported,
  getNotificationPermission,
  subscribeToPushNotifications,
  unsubscribeFromPushNotifications,
  isSubscribedToPush,
  sendTestNotification
} from '../../utils/pushNotifications';
import { User } from '../../types';

interface NotificationSettingsProps {
  currentUser: User;
}

const NotificationSettings: React.FC<NotificationSettingsProps> = ({ currentUser }) => {
  const [isSupported, setIsSupported] = useState(false);
  const [permission, setPermission] = useState<NotificationPermission>('default');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);

  useEffect(() => {
    checkNotificationStatus();
  }, []);

  const checkNotificationStatus = async () => {
    const supported = isPushNotificationSupported();
    setIsSupported(supported);
    
    if (supported) {
      const perm = getNotificationPermission();
      setPermission(perm);
      
      const subscribed = await isSubscribedToPush();
      setIsSubscribed(subscribed);
    }
  };

  const handleEnableNotifications = async () => {
    setLoading(true);
    setMessage(null);
    
    try {
      const result = await subscribeToPushNotifications(
        currentUser.id,
        currentUser.name,
        currentUser.email
      );
      
      if (result.success) {
        setIsSubscribed(true);
        setPermission('granted');
        setMessage({ type: 'success', text: 'Push notifications enabled successfully!' });
      } else {
        setMessage({ type: 'error', text: result.error || 'Failed to enable notifications' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'An error occurred while enabling notifications' });
    } finally {
      setLoading(false);
    }
  };

  const handleDisableNotifications = async () => {
    setLoading(true);
    setMessage(null);
    
    try {
      const result = await unsubscribeFromPushNotifications();
      
      if (result.success) {
        setIsSubscribed(false);
        setMessage({ type: 'success', text: 'Push notifications disabled successfully!' });
      } else {
        setMessage({ type: 'error', text: result.error || 'Failed to disable notifications' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'An error occurred while disabling notifications' });
    } finally {
      setLoading(false);
    }
  };

  const handleTestNotification = async () => {
    setLoading(true);
    setMessage(null);
    
    try {
      const result = await sendTestNotification(currentUser.id);
      
      if (result.success) {
        setMessage({ type: 'success', text: 'Test notification sent! Check your browser notifications.' });
      } else {
        setMessage({ type: 'error', text: result.error || 'Failed to send test notification' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'An error occurred while sending test notification' });
    } finally {
      setLoading(false);
    }
  };

  if (!isSupported) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center gap-3 mb-4">
          <BellOff className="w-6 h-6 text-gray-400" />
          <h2 className="text-xl font-bold text-gray-800">Push Notifications</h2>
        </div>
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-yellow-800">Not Supported</p>
              <p className="text-sm text-yellow-700 mt-1">
                Push notifications are not supported in your browser. Please use a modern browser like Chrome, Firefox, or Edge.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center gap-3 mb-6">
        <Bell className="w-6 h-6 text-blue-600" />
        <h2 className="text-xl font-bold text-gray-800">Push Notifications</h2>
      </div>

      <div className="space-y-6">
        {/* Status Card */}
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-gray-700">Current Status</span>
            {isSubscribed ? (
              <span className="flex items-center gap-2 text-green-600 text-sm font-medium">
                <CheckCircle className="w-4 h-4" />
                Enabled
              </span>
            ) : (
              <span className="flex items-center gap-2 text-gray-400 text-sm font-medium">
                <XCircle className="w-4 h-4" />
                Disabled
              </span>
            )}
          </div>
          <div className="text-sm text-gray-600">
            <p className="mb-2">
              <strong>Permission:</strong>{' '}
              <span className={`capitalize ${permission === 'granted' ? 'text-green-600' : permission === 'denied' ? 'text-red-600' : 'text-gray-600'}`}>
                {permission}
              </span>
            </p>
            <p>
              <strong>User:</strong> {currentUser.name} ({currentUser.email})
            </p>
          </div>
        </div>

        {/* Description */}
        <div className="text-sm text-gray-600">
          <p className="mb-2">
            Enable push notifications to receive instant updates about:
          </p>
          <ul className="list-disc list-inside space-y-1 text-gray-600 ml-2">
            <li>🎯 New tasks assigned to you</li>
            <li>💬 New comments on your tasks</li>
            <li>📊 Task status updates</li>
            <li>⏰ Upcoming due dates</li>
          </ul>
        </div>

        {/* Message */}
        {message && (
          <div className={`rounded-lg p-4 border ${
            message.type === 'success' ? 'bg-green-50 border-green-200' :
            message.type === 'error' ? 'bg-red-50 border-red-200' :
            'bg-blue-50 border-blue-200'
          }`}>
            <div className="flex items-start gap-3">
              {message.type === 'success' ? (
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              ) : message.type === 'error' ? (
                <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              ) : (
                <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              )}
              <p className={`text-sm ${
                message.type === 'success' ? 'text-green-800' :
                message.type === 'error' ? 'text-red-800' :
                'text-blue-800'
              }`}>
                {message.text}
              </p>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3">
          {!isSubscribed || permission !== 'granted' ? (
            <button
              onClick={handleEnableNotifications}
              disabled={loading}
              className="flex-1 flex items-center justify-center gap-2 bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              <Bell className="w-5 h-5" />
              {loading ? 'Enabling...' : 'Enable Notifications'}
            </button>
          ) : (
            <>
              <button
                onClick={handleDisableNotifications}
                disabled={loading}
                className="flex-1 flex items-center justify-center gap-2 bg-gray-600 text-white py-3 px-4 rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                <BellOff className="w-5 h-5" />
                {loading ? 'Disabling...' : 'Disable Notifications'}
              </button>
              <button
                onClick={handleTestNotification}
                disabled={loading}
                className="flex-1 flex items-center justify-center gap-2 bg-green-600 text-white py-3 px-4 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                <Send className="w-5 h-5" />
                {loading ? 'Sending...' : 'Send Test'}
              </button>
            </>
          )}
        </div>

        {/* Help Text */}
        {permission === 'denied' && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-800">Permission Denied</p>
                <p className="text-sm text-red-700 mt-1">
                  You have blocked notifications for this site. To enable them, click the lock icon in your browser's address bar and allow notifications.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationSettings;
